/**
 * Sample datasets + onboarding scenarios — one per forecasting vertical.
 *
 * These are the conversion moment: a user who picks "Inventory" on step 1
 * gets two years of believable demand with supplier halts and port delays as
 * events; a fraud analyst gets attack spikes that follow data-breach
 * headlines. Every dataset:
 *
 *   • uses real-world column names (no driver_a / driver_b),
 *   • carries 7–9 driver signals plus a sparse TEXT event column (headlines /
 *     campaign days / match results) that visibly moves the target,
 *   • is deliberately PREDICTABLE — strong weekly/yearly seasonality, lagged
 *     drivers and modest noise — so the user's first forecast looks great,
 *   • keeps genuine domain dynamics (temperature U-curves, volatility
 *     clustering, chargeback lags, booking lead times) for discovery to find,
 *   • is daily over two years — except `staffing`, which ships six months of
 *     HOURLY data to showcase sub-daily forecasting.
 *
 * Each use case also defines a SCENARIO: the pre-filled goal (target, goal
 * type, tangible horizon, name) so onboarding is category → next → next.
 * Fleet-suited verticals additionally carry a ``fleet`` block (member
 * selector pattern, rank_by_* goal type, member label) and ship a
 * population of homogeneous member columns in the sample — e.g. an
 * 18-sensor vibration fleet with three machines quietly wearing toward
 * failure — so the onboarding goal step can offer a leaderboard-style
 * first goal with a legible, designed top of the board.
 *
 * Generation is deterministic (seeded PRNG), so re-loading a sample produces
 * identical rows and dedup handles repeats cleanly.
 */

import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PaymentsIcon from '@mui/icons-material/Payments';
import LanguageIcon from '@mui/icons-material/Language';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import BoltIcon from '@mui/icons-material/Bolt';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import HotelIcon from '@mui/icons-material/Hotel';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import CreditCardOffIcon from '@mui/icons-material/CreditCardOff';
import SecurityIcon from '@mui/icons-material/Security';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import UmbrellaIcon from '@mui/icons-material/Umbrella';
import CellTowerIcon from '@mui/icons-material/CellTower';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

// ---------------------------------------------------------------------------
// PRNG + math helpers
// ---------------------------------------------------------------------------

const DAYS = 730;

// Deterministic PRNG (mulberry32).
const mulberry32 = (seed) => {
  let a = seed >>> 0;
  return () => {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

// Standard normal via Box–Muller (built on the seeded rng).
const makeRandn = (rng) => () => {
  let u = 0; let v = 0;
  while (u === 0) u = rng();
  while (v === 0) v = rng();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
};

const fmtDate = (d) => d.toISOString().slice(0, 10);
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const r0 = (v) => Math.round(v);
const r1 = (v) => Math.round(v * 10) / 10;
const r2 = (v) => Math.round(v * 100) / 100;

// Day-of-week profile peaking mid-week; weekends scaled by weekendLift.
const weekly = (date, weekendLift = 1) => {
  const dow = date.getUTCDay();
  const base = [0.72, 0.98, 1.05, 1.08, 1.1, 1.02, 0.78][dow];
  return dow === 0 || dow === 6 ? base * weekendLift : base;
};
const isWeekend = (date) => date.getUTCDay() === 0 || date.getUTCDay() === 6;

const yearly = (t, phase = 0, amp = 0.22) =>
  1 + amp * Math.sin(((2 * Math.PI) * (t + phase)) / 365.25);

const daysInMonth = (date) => new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();

// US-style holiday calendar (fixed dates + Thanksgiving / Black Friday).
const thanksgivingDay = (year) => {
  const first = new Date(Date.UTC(year, 10, 1)).getUTCDay();
  return 1 + ((4 - first + 7) % 7) + 21; // 4th Thursday of November
};
const isHoliday = (date) => {
  const m = date.getUTCMonth() + 1;
  const d = date.getUTCDate();
  if ((m === 1 && d === 1) || (m === 7 && d === 4)
    || (m === 12 && (d === 24 || d === 25 || d === 31))) return true;
  if (m === 11) {
    const tg = thanksgivingDay(date.getUTCFullYear());
    return d === tg || d === tg + 1; // Thanksgiving + Black Friday
  }
  return false;
};

// Dates ending yesterday: daily, or weekdays only (markets).
const dailyDates = () => {
  const start = new Date();
  start.setUTCHours(0, 0, 0, 0);
  start.setUTCDate(start.getUTCDate() - DAYS);
  return Array.from({ length: DAYS }, (_, t) => new Date(start.getTime() + t * 86400000));
};
const weekdayDates = () => dailyDates().filter((d) => !isWeekend(d));

// Hourly clock for the sub-daily sample: `days` full days ending yesterday.
const HOURLY_DAYS = 180;
const hourFmt = (date, h) => `${fmtDate(date)} ${String(h).padStart(2, '0')}:00`;
const hourlyStart = () => {
  const start = new Date();
  start.setUTCHours(0, 0, 0, 0);
  start.setUTCDate(start.getUTCDate() - HOURLY_DAYS);
  return start;
};

// Pull a random event with probability p; '' most days keeps the column sparse.
const maybe = (rng, p, list) => (rng() < p ? list[Math.floor(rng() * list.length)] : '');

// CSV field escaping (event texts may contain anything).
const csvField = (v) => {
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// A smooth seasonal temperature track shared by weather-driven datasets:
// AR(1) persistence around a yearly cycle (~5°C winter, ~29°C summer).
const makeTemperature = (randn) => {
  let temp = 17;
  return (t) => {
    const seasonal = 17 + 12 * Math.sin(((2 * Math.PI) * (t - 105)) / 365.25);
    temp = 0.82 * temp + 0.18 * seasonal + randn() * 1.1;
    return temp;
  };
};

// ---------------------------------------------------------------------------
// Generators — one per use case. Each returns { header, rows }.
//
// Conventions: local state carries momentum; `shock` variables decay daily so
// events have realistic multi-day aftermath; noise terms stay small (3–6%) so
// the systematic structure dominates and forecasts look credible. The event
// text column is always LAST.
// ---------------------------------------------------------------------------

const GENERATORS = {

  // ── Retail sales ─────────────────────────────────────────────────────────
  sales: (rng, randn) => {
    const EVENTS = [
      'Black Friday sale launched', 'Flash sale - 24 hours only',
      'Influencer unboxing goes viral', 'New product line drop',
      'Email campaign to 50k subscribers', 'Featured in morning TV segment',
    ];
    const temp = makeTemperature(randn);
    let visits = 3200; let adSpend = 500; let shock = 0;
    // SKU fleet: 16 per-product unit series (sku_01_units …) sharing the
    // store's promo/ad/seasonality environment. This is what the fleet goal
    // ranks by growth, so the population is DESIGNED to have a legible
    // leaderboard: three breakout SKUs compound upward over the final five
    // months, two fade, the rest live on their own seasonal cycles.
    const N_SKUS = 16;
    const skus = Array.from({ length: N_SKUS }, () => ({
      base: 40 + rng() * 260,          // resting daily units
      seasonPhase: rng() * 365,        // own seasonal timing
      promoLift: 0.15 + rng() * 0.35,  // sensitivity to promos & buzz
      drift: (rng() - 0.45) * 0.0008,  // slow secular trend
    }));
    const BREAKOUT = { 4: 0.007, 10: 0.005, 15: 0.009 }; // idx → daily log-growth
    const FADING = { 1: -0.004, 12: -0.003 };
    const BREAKOUT_START = 560; const FADE_START = 500;
    const rows = dailyDates().map((date, t) => {
      shock *= 0.75;
      const holiday = isHoliday(date);
      let event = maybe(rng, 0.028, EVENTS);
      if (date.getUTCMonth() === 10 && date.getUTCDate() === thanksgivingDay(date.getUTCFullYear()) + 1) {
        event = 'Black Friday sale launched';
      }
      if (event) shock += event.includes('viral') || event.includes('Black Friday') ? 0.22 : 0.12;
      const promo = event || holiday ? 1 : 0;
      const emailSent = date.getUTCDay() === 2 || event.includes('Email') ? 1 : 0;
      // Payday bumps: wallets open on the 1st and 15th — a crisp monthly
      // rhythm a good model picks up and a naive baseline misses.
      const payday = date.getUTCDate() === 1 || date.getUTCDate() === 15 ? 1.08 : 1;
      const price = r2(19.99 - (promo ? 3 : 0) + randn() * 0.3);
      const competitor = r2(20.49 + 0.8 * Math.sin(t / 60) + randn() * 0.3);
      const prevAd = adSpend;
      adSpend = r0(Math.max(0, (500 + t * 0.4) * (promo ? 1.8 : 1) * (1 + randn() * 0.15)));
      const prevVisits = visits;
      visits = r0((3000 + t * 1.8) * weekly(date, 1.15) * yearly(t) * payday
        * (1 + shock * 0.6) * (0.85 + 0.15 * (prevAd / 500)) * (1 + randn() * 0.03));
      const units = r0(Math.max(0,
        (420 + t * 0.35) * weekly(date, 0.85) * yearly(t, 20) * payday
        * (promo ? 1.25 : 1) * (1 - (price - competitor) * 0.03)
        * (0.85 + 0.15 * (prevVisits / 3600)) * (1 + shock)
        * (1 + randn() * 0.02)));
      const skuUnits = skus.map((sku, i) => {
        let growth = sku.drift * t;
        if (BREAKOUT[i] !== undefined && t > BREAKOUT_START) growth += BREAKOUT[i] * (t - BREAKOUT_START);
        if (FADING[i] !== undefined && t > FADE_START) growth += FADING[i] * (t - FADE_START);
        return r0(Math.max(0,
          sku.base * weekly(date, 0.85) * yearly(t, sku.seasonPhase, 0.18) * payday
          * (promo ? 1 + sku.promoLift : 1) * (1 + shock * sku.promoLift * 2)
          * Math.exp(growth) * (1 + randn() * 0.05)));
      });
      return [fmtDate(date), units, price, competitor, promo, adSpend, emailSent, visits, r1(temp(t)), holiday ? 1 : 0, ...skuUnits, event];
    });
    return {
      header: ['date', 'units_sold', 'price_usd', 'competitor_price_usd', 'promo_active', 'ad_spend_usd', 'email_sent', 'web_visits', 'temperature_c', 'is_holiday',
        ...skus.map((_, i) => `sku_${String(i + 1).padStart(2, '0')}_units`), 'marketing_event'],
      rows,
    };
  },

  // ── Inventory & replenishment ────────────────────────────────────────────
  // A live (s, S) replenishment loop: demand drains stock, POs refill it
  // after a supplier lead time, and the lead time itself moves on supply
  // events. po_received echoes po_placed by the (observable) lead — a crisp
  // discoverable lag — while demand carries the usual promo/season/payday
  // structure that makes the first forecast look great.
  inventory: (rng, randn) => {
    const EVENTS = [
      'Supplier production halt - 2 weeks', 'Port delay adds days to transit',
      'Flash sale announced - demand spike', 'Competitor stockout drives spillover demand',
      'Expedited air freight lands early', 'New supplier contract signed',
    ];
    let stock = 5200; let lead = 7; let demandEwma = 260; let shock = 0;
    const pipeline = []; // POs in flight: { arrive, qty }
    const rows = dailyDates().map((date, t) => {
      shock *= 0.85;
      const event = maybe(rng, 0.02, EVENTS);
      if (event.includes('halt')) {
        lead += 6;
        pipeline.forEach((p) => { p.arrive += 6; });
      } else if (event.includes('Port delay')) {
        lead += 4;
        pipeline.forEach((p) => { p.arrive += 4; });
      } else if (event.includes('air freight')) {
        lead = Math.max(4, lead - 3);
      }
      if (event.includes('Flash sale')) shock += 0.6;
      if (event.includes('spillover')) shock += 0.25;
      lead = clamp(0.94 * lead + 0.06 * 7 + randn() * 0.2, 3, 30);
      const holiday = isHoliday(date);
      const promo = event.includes('Flash sale') || holiday ? 1 : 0;
      const payday = date.getUTCDate() === 1 || date.getUTCDate() === 15 ? 1.06 : 1;
      const demand = r0(Math.max(0,
        (240 + t * 0.22) * weekly(date, 0.9) * yearly(t, 20) * payday
        * (promo ? 1.35 : 1) * (1 + shock) * (1 + randn() * 0.04)));
      demandEwma = 0.9 * demandEwma + 0.1 * demand;
      let arrivals = 0;
      for (let i = pipeline.length - 1; i >= 0; i -= 1) {
        if (pipeline[i].arrive <= t) { arrivals += pipeline[i].qty; pipeline.splice(i, 1); }
      }
      stock = Math.max(0, stock + arrivals - demand);
      // Reorder policy: keep (lead + 4 safety) days of cover on hand or on
      // order; each PO buys ~two weeks of expected demand.
      const onOrder = pipeline.reduce((s, p) => s + p.qty, 0);
      let po = 0;
      if (stock + onOrder < demandEwma * (lead + 4)) {
        po = r0(demandEwma * 14 * (1 + rng() * 0.1));
        pipeline.push({ arrive: t + Math.max(2, Math.round(lead + randn())), qty: po });
      }
      const daysCover = r1(stock / Math.max(1, demandEwma));
      return [fmtDate(date), demand, r0(stock), arrivals, po, r1(lead), daysCover, promo, holiday ? 1 : 0, event];
    });
    return {
      header: ['date', 'units_demanded', 'stock_on_hand_units', 'po_received_units', 'po_placed_units', 'supplier_lead_days', 'days_of_cover', 'promo_active', 'is_holiday', 'supply_event'],
      rows,
    };
  },

  // ── Revenue & finance ────────────────────────────────────────────────────
  finance: (rng, randn) => {
    const EVENTS = [
      'Enterprise client signed - 3yr deal', 'Pricing update rolled out',
      'New product tier launched', 'Payment provider outage - 6 hours',
      'Annual plan discount campaign', 'Partnership with major platform announced',
    ];
    let marketing = 900; let churn = 2.4; let shock = 0;
    const rows = dailyDates().map((date, t) => {
      shock *= 0.8;
      const event = maybe(rng, 0.03, EVENTS);
      if (event) shock += event.includes('outage') ? -0.35 : 0.25;
      if (event.includes('Enterprise')) churn = Math.max(1.2, churn - 0.2);
      churn = clamp(0.95 * churn + 0.05 * 2.4 + randn() * 0.08, 1, 5);
      const prevMarketing = marketing;
      marketing = r0((850 + t * 1.1) * (1 + randn() * 0.2));
      const newCustomers = r0(Math.max(0, 42 * weekly(date, 0.7) * (0.8 + 0.2 * (prevMarketing / 900)) * (1 + shock) * (1 + randn() * 0.06)));
      // Quarter-end close: sales teams push deals over the line in the last
      // three days of March/June/September/December — a striking, perfectly
      // forecastable calendar spike.
      const month = date.getUTCMonth();
      const qEnd = (month === 2 || month === 5 || month === 8 || month === 11)
        && date.getUTCDate() >= daysInMonth(date) - 2 ? 1.35 : 1;
      const orders = r0(Math.max(0,
        (310 + t * 0.5) * weekly(date, 0.8) * yearly(t, 15) * qEnd
        * (1 - (churn - 2.4) * 0.05) * (0.85 + 0.15 * (prevMarketing / 900))
        * (1 + shock) * (1 + randn() * 0.04)));
      const aov = r2(64 + 6 * Math.sin(((2 * Math.PI) * t) / 365.25) + randn() * 2);
      const revenue = r0(orders * aov * (1 + randn() * 0.015));
      const refunds = r0(revenue * (0.02 + Math.max(0, randn()) * 0.01));
      return [fmtDate(date), revenue, orders, aov, newCustomers, marketing, refunds, r2(churn), isHoliday(date) ? 1 : 0, event];
    });
    return {
      header: ['date', 'revenue_usd', 'orders', 'avg_order_value_usd', 'new_customers', 'marketing_spend_usd', 'refunds_usd', 'churn_rate_pct', 'is_holiday', 'business_event'],
      rows,
    };
  },

  // ── Cash flow ────────────────────────────────────────────────────────────
  // Treasury mechanics with a headline lag: receipts trail invoices by ~30
  // days (50% at 30d, 30% at 45d, 15% at 60d), payroll lands on the 1st and
  // 15th, rent on the 1st, estimated tax on fixed quarterly dates. The cash
  // balance integrates all of it — smooth, calendar-driven, forecastable.
  cashflow: (rng, randn) => {
    const EVENTS = [
      'Enterprise invoice paid early', 'Customer defaults on a large invoice',
      'Annual insurance premium due', 'Credit line drawn for expansion',
      'Early-pay discount campaign runs',
    ];
    let balance = 1850; let arOut = 2600;
    const invHist = [];
    const rows = dailyDates().map((date, t) => {
      const holiday = isHoliday(date);
      const biz = !isWeekend(date) && !holiday;
      let event = biz ? maybe(rng, 0.018, EVENTS) : '';
      const month = date.getUTCMonth();
      const day = date.getUTCDate();
      // Quarter-end close: sales pushes deals over the line, invoicing spikes.
      const qEnd = (month === 2 || month === 5 || month === 8 || month === 11)
        && day >= daysInMonth(date) - 2 ? 1.6 : 1;
      const invoices = biz
        ? r1(78 * (1 + t * 0.0006) * yearly(t, 15, 0.07) * qEnd * (1 + randn() * 0.2))
        : 0;
      invHist.push(invoices);
      const at = (lag) => invHist[invHist.length - 1 - lag] ?? 55;
      let receipts = 0;
      if (biz) {
        receipts = 0.5 * at(30) + 0.3 * at(45) + 0.15 * at(60);
        if (event.includes('paid early')) receipts += 80 + rng() * 140;
        if (event.includes('defaults')) receipts = Math.max(0, receipts - (50 + rng() * 90));
        if (event.includes('discount')) receipts *= 1.3;
        receipts = r1(Math.max(0, receipts * (1 + randn() * 0.06)));
      }
      const payrollDay = day === 1 || day === 15;
      const payroll = payrollDay ? r1(465 * (1 + t * 0.0004)) : 0;
      let outflows = biz ? 26 * (1 + randn() * 0.25) : 5 + rng() * 3;
      if (day === 1) outflows += 82; // rent + subscriptions
      if ((month === 0 || month === 3 || month === 5 || month === 8) && day === 15) {
        outflows += 240;
        event = event || 'Quarterly estimated tax paid';
      }
      if (event.includes('insurance')) outflows += 130;
      const creditDraw = event.includes('Credit line') ? 500 : 0;
      outflows = r1(outflows);
      balance = r1(balance + receipts + creditDraw - payroll - outflows);
      arOut = Math.max(0, r1(arOut + invoices - receipts));
      return [fmtDate(date), balance, receipts, invoices, payroll, outflows, arOut, payrollDay ? 1 : 0, event];
    });
    return {
      header: ['date', 'cash_balance_kusd', 'receipts_kusd', 'invoices_issued_kusd', 'payroll_kusd', 'operating_outflows_kusd', 'ar_outstanding_kusd', 'is_payroll_day', 'finance_event'],
      rows,
    };
  },

  // ── Web & app traffic ────────────────────────────────────────────────────
  traffic: (rng, randn) => {
    const EVENTS = [
      'Blog post hits Hacker News front page', 'Viral TikTok mentions the brand',
      'Reddit thread blows up', 'Newsletter feature by big creator',
      'TV interview with the founder airs', 'Google algorithm update',
    ];
    let adSpend = 400; let mentions = 4; let shock = 0;
    const rows = dailyDates().map((date, t) => {
      shock *= 0.75; // viral spikes fade over a few days
      const event = maybe(rng, 0.035, EVENTS);
      if (event) shock += event.includes('algorithm') ? -0.2 : 0.5 + rng() * 0.4;
      mentions = r1(Math.max(0.5, 0.7 * mentions + 0.3 * 4 + shock * 18 + randn() * 0.3));
      const emailSent = date.getUTCDay() === 2 || date.getUTCDay() === 4 ? 1 : 0;
      const prevAd = adSpend;
      adSpend = r0((350 + t * 0.6) * (1 + randn() * 0.15));
      const views = r0(Math.max(0,
        (14000 + t * 9) * weekly(date, 0.7) * yearly(t, 60)
        * (emailSent ? 1.08 : 1) * (0.85 + 0.15 * (prevAd / 400))
        * (1 + shock) * (1 + randn() * 0.025)));
      const visitors = r0(views * (0.55 + randn() * 0.02));
      const session = r1(clamp(3.4 + (shock > 0.3 ? -0.6 : 0) + randn() * 0.3, 0.5, 10));
      const bounce = r1(clamp(48 + shock * 10 + randn() * 3, 20, 90));
      return [fmtDate(date), views, visitors, adSpend, emailSent, mentions, session, bounce, event];
    });
    return {
      header: ['date', 'page_views', 'unique_visitors', 'ad_spend_usd', 'email_sent', 'social_mentions_k', 'avg_session_min', 'bounce_rate_pct', 'media_event'],
      rows,
    };
  },

  // ── Stocks & markets (weekdays only) ─────────────────────────────────────
  // Demo = calibrated range forecast. Two deliberately-learnable structures:
  //   • persistent volatility regimes (slow EWMA) — VIX today genuinely
  //     predicts the width of the next weeks' moves, so forecast bands breathe
  //   • post-event drift (the real "PEAD" effect) — beats keep drifting up
  //     and misses down for ~2 weeks after the headline
  stocks: (rng, randn) => {
    const EVENTS = [
      'Earnings beat estimates by 12%', 'Earnings miss - guidance cut',
      'CEO steps down unexpectedly', 'Analyst upgrade to Buy',
      'Fed raises rates 25bp', 'Product recall announced',
      'Buyback program of $2B announced', 'Activist investor discloses stake',
    ];
    const SHOCKS = { Earnings: 0.05, CEO: -0.04, Analyst: 0.025, Fed: -0.018, recall: -0.045, Buyback: 0.03, Activist: 0.035 };
    let price = 142; let spx = 4300; let sector = 1800; let yield10 = 3.8;
    let vol = 0.009; let ewmaVol = 0.009; let drift = 0; let sinceEarnings = 0;
    const rows = weekdayDates().map((date) => {
      sinceEarnings += 1;
      const earningsWeek = sinceEarnings > 58 ? 1 : 0; // quarterly cycle
      let event = maybe(rng, 0.03, EVENTS);
      if (earningsWeek && sinceEarnings > 62) {
        event = rng() < 0.7 ? 'Earnings beat estimates by 12%' : 'Earnings miss - guidance cut';
        sinceEarnings = 0;
      }
      let eventRet = 0;
      if (event) {
        const key = Object.keys(SHOCKS).find((k) => event.includes(k));
        // Headlines hit harder in stormy regimes — ties event variance to the
        // observable vol state, keeping the forecast band honest.
        eventRet = (SHOCKS[key] || 0.02) * (event.includes('miss') ? -1 : 1)
          * (0.7 + rng() * 0.6) * (0.5 + 0.5 * (vol / 0.011));
        drift = Math.sign(eventRet) * 0.006; // post-event drift, same sign
      }
      drift *= 0.88; // fades over ~2 trading weeks (the classic PEAD effect)
      // Slow-moving volatility regime (persistence 0.985, zero-mean
      // innovations): calm and stormy stretches last for weeks, so realized
      // vol — and the VIX column derived from it — genuinely forecasts the
      // width of coming moves.
      vol = clamp(0.985 * vol + 0.015 * 0.011 + (Math.abs(randn()) - 0.8) * 0.0045, 0.004, 0.04);
      yield10 = clamp(yield10 + randn() * 0.015, 2.5, 6);
      const mktRet = 0.0004 - (yield10 - 3.8) * 0.0006 + randn() * 0.003;
      const ret = 1.2 * mktRet + drift + randn() * vol + eventRet;
      price = Math.max(5, price * (1 + ret));
      spx = Math.max(1000, spx * (1 + mktRet));
      sector = Math.max(300, sector * (1 + 0.9 * mktRet + randn() * 0.003));
      ewmaVol = 0.94 * ewmaVol + 0.06 * Math.abs(ret);
      // VIX = the market's read on the CURRENT vol regime — an honest,
      // observable leading indicator for the width of the next weeks' moves.
      const vix = r2(clamp(8 + 1400 * vol + 200 * ewmaVol + randn() * 0.4, 8, 80));
      const volume = r0(3200000 * (1 + 9 * Math.abs(ret)) * (earningsWeek ? 1.3 : 1) * (1 + randn() * 0.1));
      return [fmtDate(date), r2(price), volume, r2(spx), r2(sector), vix, r2(yield10), earningsWeek, event];
    });
    return {
      header: ['date', 'close_price_usd', 'volume', 'sp500_close', 'sector_index', 'vix_close', 'treasury_10y_pct', 'earnings_week', 'market_news'],
      rows,
    };
  },

  // ── Crypto ───────────────────────────────────────────────────────────────
  crypto: (rng, randn) => {
    const EVENTS = [
      'Viral market rumor spreads on X', 'Spot ETF approval rumored',
      'Major exchange hack reported', 'Whale moves 10k BTC to exchange',
      'Country announces BTC as legal tender', 'Regulator threatens exchange lawsuit',
      'Halving countdown trends on X', 'Large fund discloses BTC allocation',
    ];
    const SIGN = (e) => (e.includes('hack') || e.includes('Whale') || e.includes('lawsuit') ? -1 : 1);
    let price = 43000; let wallets = 920; let vol = 0.016; let retEwma = 0; let trends = 40;
    let carry = 0; // news digests over ~2 days, so events echo into tomorrow
    const rows = dailyDates().map((date, t) => {
      const event = maybe(rng, 0.05, EVENTS);
      // Headlines move a frenzied market far more than a calm one — event
      // impact scales with the (observable) vol regime, so the same headline
      // that barely registers in a quiet market whipsaws a hot one.
      const regime = vol / 0.02;
      const eventRet = (event ? SIGN(event) * (0.012 + rng() * 0.014) * regime : 0) + carry;
      carry = event ? SIGN(event) * (0.007 + rng() * 0.008) * regime : 0;
      // Frenzy waves: a slow ~9-month hype cycle sets the vol baseline (calm
      // accumulation vs mania), AR persistence keeps regimes sticky, events
      // kick vol up. Volume/trends read the regime, so band width is
      // forecastable regardless of where in the cycle the sample starts.
      const volCycle = 0.009 + 0.011 * (1 + Math.sin(((2 * Math.PI) * (t - 60)) / 270));
      vol = clamp(0.9 * vol + 0.1 * volCycle + (Math.abs(randn()) - 0.8) * 0.002 + (event ? 0.004 : 0), 0.007, 0.06);
      // Mild momentum + variance drag so two years compounds to a believable
      // range instead of running away geometrically.
      const ret = 0.0004 + 0.12 * retEwma + randn() * vol + eventRet - 0.5 * vol * vol;
      price = clamp(price * (1 + ret), 15000, 150000);
      retEwma = 0.9 * retEwma + 0.1 * ret;
      trends = clamp(0.85 * trends + 0.15 * 40 + retEwma * 900 + vol * 400 + (event ? 12 : 0) + randn() * 2, 5, 100);
      const fearGreed = r0(clamp(50 + retEwma * 2200 + randn() * 3, 5, 95));
      wallets = Math.max(500, wallets * (1 + 0.0004 + (event ? 0.004 * SIGN(event) : 0) + randn() * 0.0015));
      const inflow = r0(Math.max(0, 4200 * (1 + (event.includes('Whale') ? 2.5 : 0)) * (1 - retEwma * 6) * (1 + randn() * 0.12)));
      // Volume tracks the vol REGIME (not just today's move) — an observable
      // proxy the model can use to size tomorrow's band.
      const volume = r0(18000 * (1 + 60 * vol) * (1 + 2 * Math.abs(ret)) * (1 + randn() * 0.04));
      const funding = r2(clamp(0.01 + retEwma * 2.5 + randn() * 0.003, -0.15, 0.15) * 100);
      return [fmtDate(date), r0(price), volume, r0(wallets), funding, inflow, r0(trends), fearGreed, event];
    });
    return {
      header: ['date', 'btc_price_usd', 'volume_musd', 'active_wallets_k', 'funding_rate_pct', 'exchange_inflow_btc', 'google_trends_idx', 'fear_greed_idx', 'crypto_news'],
      rows,
    };
  },

  // ── Weather & climate ────────────────────────────────────────────────────
  // Textbook synoptic mechanics, all observable a day ahead: pressure is the
  // leading indicator. Yesterday's pressure drop brings today's front — rain,
  // wind, clouds and a temperature dip — so a model reading the barometer
  // beats persistence decisively, exactly like a real forecaster does.
  weather: (rng, randn) => {
    let temp = 17; let pressure = 1013; let prevPressure = 1013;
    const rows = dailyDates().map((date, t) => {
      const seasonal = 17 + 12 * Math.sin(((2 * Math.PI) * (t - 105)) / 365.25);
      prevPressure = pressure;
      pressure = clamp(0.8 * pressure + 0.2 * 1013 + randn() * 4, 980, 1040);
      // Cold front: yesterday's low pressure pulls today's temperature down.
      const front = (prevPressure - 1013) * 0.22;
      temp = 0.8 * temp + 0.2 * seasonal + front + randn() * 0.8;
      // Yesterday's pressure drop is today's rain — a clean 1-day lead.
      const rainDrive = Math.max(0, 1010 - prevPressure);
      const precip = r1(Math.max(0, rainDrive * (0.8 + rng() * 0.4) * 1.6 + (rng() < 0.03 ? 10 + rng() * 20 : 0)));
      const cloud = r0(clamp(30 + rainDrive * 6 + precip * 1.5 + randn() * 6, 0, 100));
      const humidity = r1(clamp(55 + precip * 1.4 + cloud * 0.15 + randn() * 4, 20, 100));
      const wind = r1(Math.max(0, 12 + rainDrive * 1.1 + randn() * 3));
      const maxT = r1(temp + 4 - cloud * 0.04 - precip * 0.06);
      const minT = r1(temp - 5 + cloud * 0.02);
      let event = '';
      if (maxT > 34) event = 'Heatwave alert issued';
      else if (precip > 20) event = 'Storm front passes through';
      else if (maxT < 1 && precip > 2) event = 'First snowfall of the season';
      return [fmtDate(date), maxT, minT, humidity, wind, r1(pressure), cloud, precip, event];
    });
    return {
      header: ['date', 'max_temp_c', 'min_temp_c', 'humidity_pct', 'wind_kph', 'pressure_hpa', 'cloud_cover_pct', 'precipitation_mm', 'weather_event'],
      rows,
    };
  },

  // ── Energy & utilities ───────────────────────────────────────────────────
  energy: (rng, randn) => {
    const temp = makeTemperature(randn);
    let industrial = 100; let shock = 0;
    const rows = dailyDates().map((date, t) => {
      shock *= 0.7;
      const tc = temp(t);
      let event = '';
      if (tc > 31 && rng() < 0.35) { event = 'Heatwave drives record AC demand'; shock += 0.08; }
      else if (rng() < 0.02) { event = 'Power plant scheduled maintenance'; }
      const weekend = isWeekend(date) || isHoliday(date);
      industrial = clamp(0.93 * industrial + 0.07 * 100 + randn() * 0.8, 85, 115);
      const humidity = r1(clamp(58 + 14 * Math.sin(((2 * Math.PI) * (t + 40)) / 365.25) + randn() * 5, 20, 100));
      // Classic U-shape: heating below 16°C, cooling above 24°C (humidity
      // makes the heat feel worse — AC works harder).
      const hvac = Math.max(0, 16 - tc) * 62 + Math.max(0, tc - 24) * (88 + (humidity - 58) * 0.6);
      const demand = r0((5200 + hvac) * (weekend ? 0.88 : 1) * (industrial / 100) * (1 + shock) * (1 + randn() * 0.012));
      const solar = r0(Math.max(0, 900 * yearly(t, -80, 0.45) * (0.65 + rng() * 0.35)));
      const wind = r0(Math.max(0, 600 * (0.5 + rng() * 0.8) * (event.includes('maintenance') ? 0.7 : 1)));
      return [fmtDate(date), demand, r1(tc), humidity, solar, wind, r1(industrial), isHoliday(date) ? 1 : 0, event];
    });
    return {
      header: ['date', 'grid_demand_mwh', 'temperature_c', 'humidity_pct', 'solar_output_mwh', 'wind_output_mwh', 'industrial_activity_idx', 'is_holiday', 'grid_event'],
      rows,
    };
  },

  // ── Transportation & mobility ────────────────────────────────────────────
  transportation: (rng, randn) => {
    const EVENTS = [
      'Stadium concert downtown', 'City marathon - road closures',
      'Football derby at the arena', 'Transit workers strike',
      'Street festival in city center', 'Heavy snowfall slows service',
    ];
    const temp = makeTemperature(randn);
    let fuel = 3.4; let shock = 0;
    const rows = dailyDates().map((date, t) => {
      shock *= 0.5;
      const event = maybe(rng, 0.035, EVENTS);
      if (event) shock += event.includes('strike') ? -0.6 : event.includes('snow') ? -0.2 : 0.12;
      const downtownEvents = (event && !event.includes('strike') && !event.includes('snow') ? 1 : 0) + (rng() < 0.1 ? 1 : 0);
      fuel = clamp(fuel + randn() * 0.02 + 0.0004, 2.2, 5.5);
      const rain = r1(Math.max(0, randn() * 3 + (rng() < 0.25 ? 3 + rng() * 8 : 0)));
      const holiday = isHoliday(date);
      const month = date.getUTCMonth() + 1;
      const schoolDay = !isWeekend(date) && !holiday && month !== 7 && month !== 8 ? 1 : 0;
      const riders = r0(Math.max(0,
        480000 * (isWeekend(date) ? 0.45 : 1) * (holiday ? 0.4 : 1)
        * (schoolDay ? 1.08 : 1) * (1 - Math.min(0.12, rain * 0.008))
        * (1 + (fuel - 3.4) * 0.05) * (1 + downtownEvents * 0.03)
        * (1 + shock) * (1 + randn() * 0.02)));
      return [fmtDate(date), riders, r2(fuel), rain, r1(temp(t)), holiday ? 1 : 0, schoolDay, downtownEvents, event];
    });
    return {
      header: ['date', 'daily_riders', 'fuel_price_usd', 'rain_mm', 'max_temp_c', 'is_holiday', 'is_school_day', 'downtown_events_count', 'city_event'],
      rows,
    };
  },

  // ── Workforce & staffing (the one HOURLY sample) ─────────────────────────
  // A contact center: six months of hourly call volume with a strong
  // hour-of-day × day-of-week profile, a billing-day spike on the 3rd, and
  // agents scheduled in 8-hour blocks from last week's plan. Forecast the
  // calls, and the staffing plan writes itself — the classic WFM loop.
  staffing: (rng, randn) => {
    const EVENTS = [
      'Service outage - support lines flooded', 'National TV ad airs',
      'Major feature launch', 'Weather closure - reduced staffing',
    ];
    // Hour-of-day curve for a 24/7 desk with a 9–18h core.
    const HOD = [0.06, 0.04, 0.03, 0.03, 0.04, 0.08, 0.16, 0.34, 0.62, 0.9,
      1.0, 0.96, 0.82, 0.88, 0.94, 0.86, 0.7, 0.52, 0.38, 0.27, 0.2, 0.15, 0.11, 0.08];
    const BLOCK_LOAD = [0, 1, 2].map((b) => HOD.slice(b * 8, b * 8 + 8).reduce((a, v) => a + v, 0) / 8);
    const start = hourlyStart();
    const rows = [];
    let shock = 0; // decays hour over hour
    let base = 150; // calls in the peak hour of an average weekday
    for (let d = 0; d < HOURLY_DAYS; d += 1) {
      const date = new Date(start.getTime() + d * 86400000);
      const holiday = isHoliday(date);
      const billing = date.getUTCDate() === 3; // statements land on the 3rd
      const campaign = !billing && rng() < 0.06;
      let eventText = ''; let eventHour = -1;
      if (rng() < 0.03) {
        eventText = EVENTS[Math.floor(rng() * EVENTS.length)];
        eventHour = 8 + Math.floor(rng() * 10);
      } else if (campaign && rng() < 0.4) {
        eventText = 'National TV ad airs';
        eventHour = 19;
      }
      base *= 1 + 0.0002 + randn() * 0.0008; // slow secular growth
      const dayLevel = base * weekly(date, 0.5) * yearly(d, 30, 0.12)
        * (holiday ? 0.55 : 1) * (billing ? 1.45 : 1) * (campaign ? 1.2 : 1);
      for (let h = 0; h < 24; h += 1) {
        const isEvent = h === eventHour;
        if (isEvent) {
          shock += eventText.includes('outage') ? 1.6
            : eventText.includes('closure') ? -0.5 : 0.45;
        }
        shock *= 0.82;
        const calls = r0(Math.max(0, dayLevel * HOD[h] * (1 + shock) * (1 + randn() * 0.06)));
        // Agents come from the demand PLAN (no same-day reaction): enough
        // heads for the block's average load at ~10 calls/agent/hour.
        const agents = Math.max(2, Math.round((dayLevel * BLOCK_LOAD[Math.floor(h / 8)]) / 10));
        const aht = r0(320 + (shock > 0.8 ? 70 : 0) + randn() * 12);
        const capacity = agents * (3600 / aht) * 0.9;
        const sl = r1(clamp(
          100 * Math.min(1, capacity / Math.max(1, calls)) - (shock > 0.8 ? 8 : 0) + randn() * 1.5,
          20, 100,
        ));
        rows.push([hourFmt(date, h), calls, agents, aht, sl, campaign ? 1 : 0, holiday ? 1 : 0, isEvent ? eventText : '']);
      }
    }
    return {
      header: ['date', 'calls_offered', 'agents_scheduled', 'avg_handle_time_sec', 'service_level_pct', 'email_campaign', 'is_holiday', 'ops_event'],
      rows,
    };
  },

  // ── Supply chain & logistics ─────────────────────────────────────────────
  logistics: (rng, randn) => {
    const EVENTS = [
      'Container ship blocks major canal', 'Port workers strike enters day 1',
      'Typhoon closes hub port', 'New import tariffs announced',
      'Peak season surcharges kick in', 'Carrier adds extra capacity',
    ];
    let rate = 2400; let congestion = 32; let fuel = 3.1; let utilization = 78; let shock = 0;
    const rows = dailyDates().map((date, t) => {
      shock *= 0.965; // supply-chain disruptions unwind slowly
      const event = maybe(rng, 0.02, EVENTS);
      if (event) shock += event.includes('capacity') ? -0.08 : 0.15 + rng() * 0.25;
      congestion = clamp(0.9 * congestion + 0.1 * 32 + shock * 40 + randn() * 2, 5, 100);
      const vessels = r0(Math.max(0, congestion * 0.6 + randn() * 2));
      fuel = clamp(fuel + randn() * 0.02, 2, 5);
      const seasonal = yearly(t, -60, 0.12); // pre-holiday shipping peak
      utilization = clamp(0.9 * utilization + 0.1 * (78 * seasonal) + shock * 10 + randn() * 1, 50, 100);
      rate = Math.max(900, 0.93 * rate + 0.07 * (2400 * seasonal * (1 + shock)) + congestion * 4 + (fuel - 3.1) * 120 + randn() * 25);
      const orders = r1(100 * seasonal * (1 + randn() * 0.03));
      return [fmtDate(date), r0(rate), r1(congestion), vessels, r2(fuel), orders, r1(utilization), event];
    });
    return {
      header: ['date', 'container_rate_usd', 'port_congestion_idx', 'vessels_waiting', 'fuel_price_usd', 'orders_index', 'warehouse_utilization_pct', 'disruption_event'],
      rows,
    };
  },

  // ── Manufacturing & IoT ──────────────────────────────────────────────────
  manufacturing: (rng, randn) => {
    const BREAKDOWNS = ['Line 2 conveyor breakdown', 'CNC spindle failure', 'Hydraulic press fault'];
    const OTHERS = ['Preventive maintenance window', 'New robot cell commissioned', 'Supplier delivers substandard batch'];
    let defect = 1.8; let wearing = 0; let backlog = 9000;
    // Sensor fleet: 18 per-machine vibration series (sensor_01_vibration …).
    // Most machines get preventive maintenance that resets their wear, so
    // their vibration mean-reverts; three "critical" machines skip their
    // last service and their vibration climbs through the final two months
    // — exactly the members a rank-by-risk fleet goal should surface.
    const N_SENSORS = 18;
    const sensors = Array.from({ length: N_SENSORS }, () => ({
      base: 16 + rng() * 10,           // resting vibration level
      wearRate: 0.02 + rng() * 0.03,   // hidden wear accumulation per day
      loadCoef: 0.5 + rng(),           // sensitivity to shift load
      wear: rng() * 1.5,
      sinceService: Math.floor(rng() * 60),
    }));
    const CRITICAL = [2, 9, 16];       // skip maintenance from CRITICAL_START on
    const CRITICAL_START = 640;        // ~3 months before the sample ends
    const rows = dailyDates().map((date, t) => {
      const weekend = isWeekend(date);
      wearing += 0.03 + rng() * 0.02;
      const shifts = weekend ? 1 : 3;
      let event = '';
      let downtime = r1(Math.max(0, randn() * 0.4));
      // Vibration and defects both rise with hidden wear — discoverable leads.
      const vibration = r1(clamp(20 + wearing * 9 + randn() * 1.5, 10, 80));
      defect = clamp(1.5 + wearing * 0.35 + randn() * 0.15, 0.8, 8);
      if (wearing > 3 && rng() < 0.3) {
        event = BREAKDOWNS[Math.floor(rng() * BREAKDOWNS.length)];
        downtime = r1(6 + rng() * 8);
        wearing = 0;
      } else if (rng() < 0.02) {
        event = OTHERS[Math.floor(rng() * OTHERS.length)];
        if (event.includes('maintenance')) { downtime = r1(4 + rng() * 3); wearing = Math.max(0, wearing - 2); }
        if (event.includes('substandard')) defect = clamp(defect + 1.5, 0.8, 8);
      }
      downtime = Math.min(downtime, shifts * 8);
      const units = r0(Math.max(0, shifts * 1180 * (1 - downtime / (shifts * 8)) * (1 - defect / 100) * (1 + randn() * 0.015)));
      backlog = Math.max(2000, backlog + r0(950 * weekly(date, 0.6)) - units + randn() * 60);
      const material = r1(100 + 8 * Math.sin(date.getTime() / 8e9) + randn() * 1);
      const sensorVib = sensors.map((s, i) => {
        s.sinceService += 1;
        const skipService = CRITICAL.includes(i) && t > CRITICAL_START;
        // Unserviced bearings degrade faster the longer they run — the
        // classic accelerating wear curve maintenance teams watch for.
        s.wear += s.wearRate * (0.8 + shifts / 3) * (skipService ? 2.5 : 1) + rng() * 0.01;
        // Preventive service every ~70 days resets wear — unless skipped.
        if (!skipService && s.sinceService > 65 && rng() < 0.25) {
          s.wear = rng() * 0.8;
          s.sinceService = 0;
        }
        return r1(clamp(s.base + s.wear * 2.2 + s.loadCoef * shifts + randn() * 0.9, 8, 120));
      });
      return [fmtDate(date), units, downtime, r2(defect), vibration, material, shifts, r0(backlog), ...sensorVib, event];
    });
    return {
      header: ['date', 'units_produced', 'downtime_hours', 'defect_rate_pct', 'machine_vibration_idx', 'material_cost_idx', 'shifts_scheduled', 'order_backlog_units',
        ...sensors.map((_, i) => `sensor_${String(i + 1).padStart(2, '0')}_vibration`), 'factory_event'],
      rows,
    };
  },

  // ── Real estate ──────────────────────────────────────────────────────────
  // Partial-adjustment market: listing prices drift a few percent per month
  // toward an affordability fundamental set by mortgage rates. The DIRECTION
  // of tomorrow's drift is readable from today's observable gap (rates, buyer
  // search, days-on-market), so a model beats carry-forward decisively while
  // the series still looks like slow, smooth housing data.
  realestate: (rng, randn) => {
    const EVENTS = [
      'Central bank hikes rates 50bp', 'Central bank cuts rates 25bp',
      'New waterfront development approved', 'Property tax reform proposed',
      'Tech employer announces 5k local jobs',
    ];
    let rate = 3.6; let rateEwma = 3.6; let search = 100; let price = 452000; let jobs = 1;
    const rows = dailyDates().map((date, t) => {
      const event = maybe(rng, 0.012, EVENTS);
      if (event.includes('hikes')) rate += 0.25;
      if (event.includes('cuts')) rate -= 0.2;
      if (event.includes('jobs')) jobs *= 1.012;
      rate = clamp(rate + 0.0012 + randn() * 0.008, 2.5, 8);
      rateEwma = 0.97 * rateEwma + 0.03 * rate; // affordability digests slowly
      const spring = yearly(t, 80, 0.05);
      // Buyer search interest reacts to rates FAST — it leads prices.
      search = clamp(0.9 * search + 0.1 * (100 - (rate - 4) * 18) * spring + randn() * 1.2, 30, 170);
      // Affordability fundamental: each rate point above 4% is worth ~9% of
      // price. Listings close ~3%/day of the gap — smooth and forecastable.
      const fundamental = 452000 * (1 + t * 0.00006) * spring * (1 - 0.09 * (rateEwma - 4)) * jobs;
      price = price + 0.035 * (fundamental - price) + price * randn() * 0.0004;
      const sqft = r0(price / (1850 + randn() * 8));
      const listings = r0(Math.max(0, 68 * yearly(t, 80, 0.3) * weekly(date, 0.5) * (1 + randn() * 0.08)));
      const sold = r0(Math.max(0, 52 * spring * (1 - 0.08 * (rateEwma - 4)) * (search / 100) * weekly(date, 0.4) * (1 + randn() * 0.08)));
      const dom = r0(clamp(28 + (rateEwma - 4) * 9 + randn() * 1.5, 7, 120));
      return [fmtDate(date), r0(price), listings, sold, r2(rate), dom, sqft, r1(search), event];
    });
    return {
      header: ['date', 'median_list_price_usd', 'new_listings', 'homes_sold', 'mortgage_rate_pct', 'days_on_market', 'price_per_sqft_usd', 'buyer_search_idx', 'market_event'],
      rows,
    };
  },

  // ── Healthcare ───────────────────────────────────────────────────────────
  // Epidemic surveillance chain with crisp, actionable lags: the virus shows
  // up in wastewater a full week before people get sick enough to be counted
  // as reported cases — exactly how real public-health forecasting works.
  healthcare: (rng, randn) => {
    const temp = makeTemperature(randn);
    let flu = 40; let wastewater = 25; let shock = 0;
    const wwHist = Array(7).fill(25);
    const rows = dailyDates().map((date, t) => {
      shock *= 0.9;
      const tc = temp(t);
      const fluSeason = Math.max(0, -Math.sin(((2 * Math.PI) * (t - 105)) / 365.25));
      wastewater = Math.max(5, 0.85 * wastewater + 0.15 * (25 + 260 * fluSeason) + randn() * 8);
      wwHist.push(wastewater);
      // Reported cases mirror the wastewater signal from 7 days ago.
      flu = Math.max(5, 0.5 * flu + 0.5 * (wwHist[wwHist.length - 8] * 1.4) + randn() * 8);
      let event = '';
      if (flu > 330 && rng() < 0.2) { event = 'Flu outbreak declared in county'; shock += 0.12; }
      else if (tc > 32 && rng() < 0.25) { event = 'Heat advisory - dehydration risk'; shock += 0.08; }
      else if (rng() < 0.008) { event = 'City marathon - injury influx'; shock += 0.1; }
      const aqi = r0(clamp(45 + 25 * Math.max(0, Math.sin(((2 * Math.PI) * (t - 200)) / 365.25)) + randn() * 8, 10, 240));
      const pollen = r0(clamp(20 + 60 * Math.max(0, Math.sin(((2 * Math.PI) * (t - 80)) / 365.25)) + randn() * 6, 0, 120));
      const admissions = r0(Math.max(0,
        (300 + flu * 0.16 + Math.max(0, aqi - 100) * 0.35 + pollen * 0.08 + Math.max(0, tc - 30) * 4)
        * (isWeekend(date) ? 1.12 : 1) * (1 + shock) * (1 + randn() * 0.018)));
      return [fmtDate(date), admissions, r0(flu), r0(wastewater), aqi, pollen, r1(tc), isWeekend(date) ? 1 : 0, event];
    });
    return {
      header: ['date', 'er_admissions', 'flu_cases_reported', 'wastewater_viral_idx', 'air_quality_idx', 'pollen_idx', 'max_temp_c', 'is_weekend', 'health_event'],
      rows,
    };
  },

  // ── Sports ───────────────────────────────────────────────────────────────
  sports: (rng, randn) => {
    const rows = [];
    let shock = 0; let form = 0.55; let position = 8;
    dailyDates().forEach((date) => {
      shock *= 0.82;
      form = clamp(form + randn() * 0.015, 0.25, 0.8);
      position = clamp(Math.round(0.95 * position + 0.05 * (21 - form * 25) + randn() * 0.4), 1, 20);
      const matchDay = date.getUTCDay() === 3 || date.getUTCDay() === 6 ? 1 : 0;
      let event = '';
      if (matchDay) {
        const goalsFor = Math.floor(rng() * 4);
        const goalsAgainst = Math.floor(rng() * 3);
        const won = rng() < form ? 1 : 0;
        event = won
          ? `Won ${Math.max(goalsFor, goalsAgainst + 1)}-${goalsAgainst} at home`
          : `Lost ${Math.min(goalsFor, goalsAgainst)}-${Math.max(goalsAgainst, goalsFor + 1)} away`;
        shock += won ? 0.3 : -0.12;
      } else if (rng() < 0.01) {
        event = rng() < 0.5 ? 'Star striker signing announced' : 'Head coach sacked after poor run';
        shock += event.includes('signing') ? 0.7 : -0.25;
      }
      const mentions = r1(Math.max(1, 18 * (1 + Math.abs(shock) * 2.2) * (1 + randn() * 0.06)));
      const searches = r0(Math.max(0, 5200 * (matchDay ? 1.9 : 1) * (1 + shock) * (1 + randn() * 0.04)));
      const jerseys = r0(Math.max(0, 900 * (1 + shock * 1.4) * (21 - position) / 13 * (1 + randn() * 0.06)));
      const merch = r0(Math.max(0, 24000 * (matchDay ? 1.5 : 1) * (1 + shock) * ((21 - position) / 13) ** 0.3 * (1 + randn() * 0.03)));
      rows.push([fmtDate(date), merch, mentions, searches, jerseys, position, matchDay, event]);
    });
    return {
      header: ['date', 'merch_sales_usd', 'social_mentions_k', 'ticket_searches', 'jersey_searches', 'league_position', 'is_match_day', 'match_event'],
      rows,
    };
  },

  // ── Gaming & apps ────────────────────────────────────────────────────────
  // Live-service content cycle: a new season ships every 90 days like
  // clockwork. Engagement spikes at launch and decays along a smooth curve —
  // with days_since_update in the data, the whole sawtooth is forecastable
  // months ahead, which makes a spectacular multi-week demo.
  gaming: (rng, randn) => {
    let dau = 180000; let shock = 0; let sinceSeason = 30; let twitch = 40;
    const rows = dailyDates().map((date) => {
      shock *= 0.94;
      sinceSeason += 1;
      let event = '';
      if (sinceSeason >= 90) { event = 'New season launched with battle pass'; sinceSeason = 0; }
      else if (rng() < 0.025) { event = 'Top streamer plays live to 300k viewers'; shock += 0.18; }
      else if (rng() < 0.012) { event = 'Server outage - 3 hours downtime'; shock -= 0.22; }
      else if (rng() < 0.02) { event = 'Double XP weekend announced'; shock += 0.12; }
      // Content freshness: +45% at launch, decaying with a 3-week half-life —
      // the deterministic sawtooth the model should nail.
      const freshness = 1 + 0.4 * Math.exp(-sinceSeason / 30);
      twitch = r1(Math.max(3, 0.75 * twitch + 0.25 * 40 + (freshness - 1) * 130 + shock * 90 + randn() * 2));
      const adSpend = r0(2500 * (1 + randn() * 0.25) * (sinceSeason < 7 ? 2 : 1));
      dau = Math.max(20000, dau * (1 + 0.0006 + randn() * 0.003));
      const activeToday = r0(dau * (isWeekend(date) ? 1.3 : 1) * freshness * (1 + shock) * (1 + randn() * 0.012));
      const installs = r0(Math.max(0, 3800 * freshness * (1 + shock * 1.6) * (1 + adSpend / 5000) * (1 + randn() * 0.08)));
      const session = r1(clamp(34 * (0.9 + freshness * 0.1) * (1 + shock * 0.3) + randn() * 1.2, 8, 90));
      const iap = r0(activeToday * 0.062 * (1 + (sinceSeason < 14 ? 0.35 : 0)) * (1 + randn() * 0.05));
      return [fmtDate(date), activeToday, installs, session, adSpend, twitch, iap, sinceSeason, event];
    });
    return {
      header: ['date', 'daily_active_users', 'new_installs', 'avg_session_min', 'ad_spend_usd', 'twitch_viewers_k', 'iap_revenue_usd', 'days_since_update', 'game_event'],
      rows,
    };
  },

  // ── Travel & hospitality ─────────────────────────────────────────────────
  tourism: (rng, randn) => {
    const EVENTS = [
      'Music festival week begins', 'International trade fair in town',
      'Airline opens direct route', 'Cruise ship day - 4k visitors',
      'Storm cancels inbound flights',
    ];
    let shock = 0;
    const rows = dailyDates().map((date, t) => {
      shock *= 0.85;
      const event = maybe(rng, 0.025, EVENTS);
      if (event) shock += event.includes('Storm') ? -0.25 : event.includes('route') ? 0.08 : 0.2;
      const season = yearly(t, -80, 0.28); // summer peak
      const holiday = isHoliday(date);
      const rain = r1(Math.max(0, randn() * 2.5 + (rng() < 0.2 ? 3 + rng() * 8 : 0)));
      const occupancy = r1(clamp(58 * season * (isWeekend(date) ? 1.18 : 1) * (holiday ? 1.15 : 1) * (1 + shock) + randn() * 1.8, 15, 100));
      const adr = r0(145 * (0.7 + occupancy / 160) * (1 + randn() * 0.03));
      // Searches lead stays by ~3 weeks — the seasonal phase runs early and
      // deliberately ignores today's shock (bookers plan ahead).
      const searches = r1(Math.max(1, 40 * yearly(t + 21, -80, 0.28) * (1 + randn() * 0.04)));
      const arrivals = r1(Math.max(0.5, 12 * season * (holiday ? 1.2 : 1) * (1 + shock) * (1 + randn() * 0.07)));
      const leadDays = r0(clamp(24 + (season - 1) * 40 + randn() * 2, 5, 60));
      return [fmtDate(date), occupancy, adr, searches, leadDays, arrivals, holiday ? 1 : 0, rain, event];
    });
    return {
      header: ['date', 'occupancy_rate_pct', 'avg_daily_rate_usd', 'flight_searches_k', 'booking_lead_days', 'arrivals_k', 'is_holiday', 'rain_mm', 'city_event'],
      rows,
    };
  },

  // ── Agriculture & food ───────────────────────────────────────────────────
  agriculture: (rng, randn) => {
    const EVENTS = [
      'Major exporter announces grain export ban', 'Record harvest reported in key region',
      'Drought warning for growing belt', 'Fertilizer prices spike on gas costs',
      'Trade deal reopens export corridor',
    ];
    // Partial-adjustment commodity: price converges ~3%/day toward a supply
    // fundamental that is FULLY observable — harvest calendar + soil-moisture
    // drought pressure + event shocks (which show up in fertilizer costs and
    // export volumes). No hidden anchors: a model that reads the columns can
    // price the convergence, which is where the demo skill comes from.
    let price = 6.2; let rainEwma = 2.2; let soil = 55; let shock = 0;
    const rows = dailyDates().map((date, t) => {
      shock *= 0.975; // supply shocks reprice the market for weeks
      const event = maybe(rng, 0.015, EVENTS);
      if (event) {
        if (event.includes('ban') || event.includes('Drought') || event.includes('Fertilizer')) shock += 0.18 + rng() * 0.25;
        else shock -= 0.14 + rng() * 0.15;
      }
      const wetSeason = Math.max(0, Math.sin(((2 * Math.PI) * (t - 60)) / 365.25));
      const rain = r1(Math.max(0, 1.2 + 4.5 * wetSeason * rng() + randn() * 0.7));
      rainEwma = 0.97 * rainEwma + 0.03 * rain;
      soil = clamp(0.95 * soil + rain * 1.6 - 1.8 + randn() * 0.8, 10, 95); // moisture integrates rain
      const droughtPressure = Math.max(0, 1.8 - rainEwma);
      const harvestDip = yearly(t, -170, 0.12); // post-harvest supply glut
      const fundamental = 6.2 * harvestDip * (1 + shock * 0.6) * (1 + droughtPressure * 0.15);
      price = clamp(0.96 * price + 0.04 * fundamental + price * randn() * 0.0015, 3, 14);
      const exportVol = r0(Math.max(20, 140 * harvestDip * (1 - shock * 0.5) * (1 + randn() * 0.05)));
      // Fertilizer is the clean, observable gauge of the supply shock — the
      // column a model reads to price the weeks-long convergence.
      const fertilizer = r1(100 + shock * 60 + randn() * 1);
      const temp = r1(17 + 12 * Math.sin(((2 * Math.PI) * (t - 105)) / 365.25) + randn() * 1.5);
      return [fmtDate(date), r2(price), rain, temp, r1(soil), fertilizer, exportVol, event];
    });
    return {
      header: ['date', 'wheat_price_usd_bushel', 'rainfall_mm', 'temperature_c', 'soil_moisture_idx', 'fertilizer_cost_idx', 'export_volume_kt', 'market_news'],
      rows,
    };
  },

  // ── Fraud prevention ─────────────────────────────────────────────────────
  fraud: (rng, randn) => {
    const EVENTS = [
      'Major retailer data breach disclosed', 'New phishing kit circulating on forums',
      'Card dump posted on dark web market', 'Bot-driven signup wave detected',
      'Regional carrier SIM-swap ring busted',
    ];
    let shock = 0;
    const fraudHistory = [];
    const rows = dailyDates().map((date, t) => {
      shock *= 0.88; // breach fallout lasts ~2 weeks
      const event = maybe(rng, 0.02, EVENTS);
      if (event) shock += event.includes('busted') ? -0.2 : 0.5 + rng() * 0.9;
      const txns = r1(Math.max(1, 240 * weekly(date, 1.05) * yearly(t, 20, 0.1) * (1 + randn() * 0.03)));
      const loginFails = r1(Math.max(0.5, 6 * (1 + shock * 1.8) * (1 + randn() * 0.1)));
      const vpnShare = r1(clamp(8 + shock * 9 + randn() * 0.8, 2, 40));
      const attempts = r0(Math.max(0, txns * 1000 * 0.0016 * (1 + shock) * (1 + randn() * 0.06)));
      fraudHistory.push(attempts);
      // Chargebacks surface ~7 days after the fraud that caused them.
      const lagged = fraudHistory[Math.max(0, fraudHistory.length - 8)] || attempts;
      const chargebacks = r0(Math.max(0, lagged * 0.22 * (1 + randn() * 0.08)));
      const newAccounts = r0(Math.max(0, 800 * (1 + (shock > 0.4 ? shock : 0)) * (1 + randn() * 0.06)));
      const avgTxn = r2(58 + (shock > 0.5 ? 22 : 0) + randn() * 2.5); // fraudsters go big
      return [fmtDate(date), attempts, txns, chargebacks, newAccounts, loginFails, vpnShare, avgTxn, event];
    });
    return {
      header: ['date', 'fraud_attempts', 'transactions_k', 'chargebacks', 'new_accounts', 'login_failures_k', 'vpn_traffic_pct', 'avg_transaction_usd', 'threat_intel_event'],
      rows,
    };
  },

  // ── Cybersecurity ────────────────────────────────────────────────────────
  cybersecurity: (rng, randn) => {
    const EVENTS = [
      'Zero-day disclosed in popular VPN', 'Ransomware campaign targets sector',
      'Botnet takedown by law enforcement', 'Critical patch Tuesday - 3 exploited CVEs',
      'Credential dump of 2M accounts leaked',
    ];
    let chatter = 30; let compliance = 84; let shock = 0;
    const shockHist = [0, 0, 0]; // campaigns take ~3 days to weaponize
    const cveHist = [9, 9];
    const rows = dailyDates().map((date, t) => {
      shock *= 0.85;
      const event = maybe(rng, 0.025, EVENTS);
      if (event) shock += event.includes('takedown') ? -0.25 : 0.22 + rng() * 0.3;
      shockHist.push(shock);
      // Dark-web chatter spikes the DAY the threat emerges; the attack wave
      // lands three days later — a crisp lead the discovery engine can find
      // and the SOC can actually act on.
      const lagShock = shockHist[shockHist.length - 4];
      chatter = clamp(0.3 * chatter + 0.7 * (30 + shock * 55) + randn() * 1.5, 5, 100);
      compliance = clamp(0.98 * compliance + 0.02 * 84 + (event.includes('patch') ? 3 : 0) - (shock > 0.5 ? 1 : 0) + randn() * 0.3, 60, 98);
      const cves = r0(Math.max(0, 9 + (date.getUTCDay() === 2 ? 14 : 0) + randn() * 3)); // patch Tuesday bump
      cveHist.push(cves);
      // Fresh CVEs get weaponized within ~2 days.
      const cveDrive = 1 + (cveHist[cveHist.length - 3] - 9) * 0.006;
      const intrusions = r0(Math.max(0,
        (11000 + t * 6) * (isWeekend(date) ? 0.8 : 1)
        * (1 + lagShock) * cveDrive
        * (1 - (compliance - 84) * 0.008) * (1 + randn() * 0.02)));
      const phishing = r0(Math.max(0, 5200 * (1 + lagShock * 0.7) * (1 + randn() * 0.04)));
      const malware = r0(Math.max(0, 240 * (1 + lagShock * 1.1) * (1 + randn() * 0.06)));
      const failedLogins = r1(Math.max(0.5, 14 * (1 + lagShock * 0.9) * (isWeekend(date) ? 0.7 : 1) * (1 + randn() * 0.05)));
      return [fmtDate(date), intrusions, phishing, cves, r1(chatter), malware, failedLogins, r1(compliance), event];
    });
    return {
      header: ['date', 'intrusion_attempts', 'phishing_blocked', 'cves_published', 'dark_web_chatter_idx', 'malware_detections', 'failed_logins_k', 'patch_compliance_pct', 'threat_event'],
      rows,
    };
  },

  // ── Insurance ────────────────────────────────────────────────────────────
  insurance: (rng, randn) => {
    let policies = 210; let stormTail = 0;
    const rows = dailyDates().map((date, t) => {
      stormTail *= 0.6; // claims keep arriving days after the storm
      const stormSeason = Math.max(0.2, Math.sin(((2 * Math.PI) * (t - 40)) / 365.25) + 0.5);
      let severity = 0;
      let event = '';
      const rain = r1(Math.max(0, randn() * 2 + stormSeason * 2 * rng()));
      let wind = r1(Math.max(0, 14 + randn() * 4));
      if (rng() < 0.045 * stormSeason) {
        severity = r1(1 + rng() * 9);
        wind = r1(wind + severity * 6);
        event = severity > 7 ? 'Severe hailstorm hits metro area'
          : severity > 4 ? 'Flood warning after heavy rain'
            : 'Windstorm causes scattered damage';
        stormTail += severity;
      }
      policies = Math.min(400, policies * (1 + 0.0003));
      const claims = r0(Math.max(0,
        (policies * 0.55) * weekly(date, 0.6)
        + stormTail * 34 + rain * 1.2 + randn() * 5));
      const avgClaim = r0(3800 * (1 + stormTail * 0.06) * (1 + randn() * 0.05));
      return [fmtDate(date), claims, severity, rain, wind, r1(policies), avgClaim, event];
    });
    return {
      header: ['date', 'claims_filed', 'storm_severity', 'rainfall_mm', 'wind_kph', 'policies_active_k', 'avg_claim_usd', 'weather_event'],
      rows,
    };
  },

  // ── Telecom & networks ───────────────────────────────────────────────────
  telecom: (rng, randn) => {
    let subs = 1240; let stepUp = 1; let shock = 0;
    const rows = dailyDates().map((date, t) => {
      shock *= 0.6;
      let event = '';
      let outage = 0;
      if (date.getUTCDay() === 5 && rng() < 0.3) { event = 'Streaming premiere night - blockbuster release'; shock += 0.1; }
      else if (rng() < 0.008) { event = 'Regional outage - 2 hours'; shock -= 0.25; outage = r0(60 + rng() * 120); }
      else if (rng() < 0.006) { event = '5G rollout completed in new district'; stepUp *= 1.015; }
      subs = Math.min(2000, subs * (1 + 0.0004 + randn() * 0.0004));
      const traffic = r1(Math.max(1,
        (820 + t * 0.5) * stepUp * (isWeekend(date) ? 1.18 : 1)
        * (subs / 1240) * (1 + shock) * (1 + randn() * 0.025)));
      const videoShare = r1(clamp(64 + shock * 8 + (isWeekend(date) ? 3 : 0) + randn() * 1.5, 30, 90));
      const gamingShare = r1(clamp(12 + (isWeekend(date) ? 4 : 0) + randn() * 1, 4, 30));
      const speed = r1(clamp(180 * stepUp * (1 - Math.max(0, shock) * 0.2) - outage * 0.1 + randn() * 6, 40, 400));
      return [fmtDate(date), traffic, r0(subs), videoShare, gamingShare, speed, outage, isWeekend(date) ? 1 : 0, event];
    });
    return {
      header: ['date', 'data_traffic_tb', 'subscribers_k', 'video_share_pct', 'gaming_share_pct', 'avg_speed_mbps', 'outage_minutes', 'is_weekend', 'network_event'],
      rows,
    };
  },

  // ── Something else — a relatable local-business metric ───────────────────
  other: (rng, randn) => {
    const EVENTS = [
      'Local street festival nearby', 'Featured in city food guide',
      'Rainstorm keeps people home', 'New office building opens next door',
      'Competitor closes for renovation',
    ];
    const temp = makeTemperature(randn);
    let shock = 0; let baseline = 340; let foot = 100;
    const rows = dailyDates().map((date, t) => {
      shock *= 0.8;
      const event = maybe(rng, 0.025, EVENTS);
      if (event) {
        if (event.includes('Rainstorm')) shock -= 0.18;
        else if (event.includes('office') || event.includes('Competitor')) baseline *= 1.02;
        else shock += 0.25;
      }
      const rain = r1(Math.max(0, randn() * 2.5 + (rng() < 0.2 ? 4 + rng() * 8 : 0)));
      // Café-style week: quiet Mondays, packed Fri–Sun.
      const dowLift = [1.25, 0.82, 0.88, 0.92, 1.0, 1.18, 1.32][date.getUTCDay()];
      foot = r1(clamp(0.75 * foot + 0.25 * (100 * dowLift) * (1 - rain * 0.008) * (1 + shock * 0.5) + randn() * 1.2, 30, 220));
      const marketing = r0(Math.max(0, 120 * (1 + randn() * 0.25) + (rng() < 0.08 ? 400 : 0)));
      const ticket = r2(14.5 + (isWeekend(date) ? 1.8 : 0) + randn() * 0.4);
      const customers = r0(Math.max(0,
        baseline * dowLift * (isHoliday(date) ? 1.2 : 1)
        * (foot / 100) * (1 + shock) * (1 + marketing / 5000) * (1 + randn() * 0.018)));
      return [fmtDate(date), customers, marketing, r1(foot), ticket, r1(temp(t)), rain, isHoliday(date) ? 1 : 0, event];
    });
    return {
      header: ['date', 'daily_customers', 'marketing_spend_usd', 'foot_traffic_idx', 'avg_ticket_usd', 'temperature_c', 'rain_mm', 'is_holiday', 'local_event'],
      rows,
    };
  },
};

// ---------------------------------------------------------------------------
// Use-case catalog — mirrors the verticals the public site actually sells to
// (operational planning: demand, inventory, cash, staffing, supply chain).
// Each entry carries the full onboarding SCENARIO: derived workspace name,
// pre-filled goal (type + tangible horizon + name) and the sample dataset.
// Order matters twice: the flat array feeds the chat picker grid (first ~8
// are what most users ever see), and USE_CASE_GROUPS below feeds the grouped
// pickers — both lead with the ICP cases and bury the hobbyist magnets.
// ---------------------------------------------------------------------------

export const USE_CASES = [
  {
    id: 'sales', label: 'Sales & demand', Icon: ShoppingCartIcon,
    workspaceName: 'Retail Sales Forecasting',
    targets: ['Units sold', 'Store footfall', 'Order value', 'Promotion lift', 'Returns volume'],
    goalText: 'Forecast product sales and demand', goalName: 'Units sold — 2 weeks ahead',
    scenario: {
      goalType: 'forecast_value', horizonDays: 14, horizonOptions: [7, 14, 30],
      pitch: 'Forecast daily units sold two weeks out, driven by pricing, promos, web traffic, weather and marketing events.',
      // The sample also ships a 16-SKU fleet; the goal step offers ranking
      // the catalog as an alternative to forecasting total units.
      fleet: {
        goalType: 'rank_by_growth', horizonDays: 14,
        selector: { type: 'pattern', value: 'sku_*_units' },
        goalName: 'SKU catalog — rank by growth',
        membersLabel: 'SKUs',
        pitch: 'Rank all 16 SKUs by predicted growth — a leaderboard of which products are about to take off, refreshed on live data.',
      },
    },
    sample: {
      filename: 'sample_retail_sales.csv', targetColumn: 'units_sold',
      description: 'Two years of daily retail sales with pricing vs competitor, promos, ad spend, web traffic, weather, per-SKU unit sales for a 16-product catalog — and marketing moments as events.',
    },
  },
  {
    id: 'inventory', label: 'Inventory & replenishment', Icon: Inventory2Icon,
    workspaceName: 'Inventory Forecasting',
    targets: ['Units demanded', 'Stock on hand', 'Stockout risk', 'Reorder timing', 'Supplier lead times'],
    goalText: 'Forecast demand and stock levels', goalName: 'Units demanded — 2 weeks ahead',
    scenario: {
      goalType: 'forecast_value', horizonDays: 14, horizonOptions: [7, 14, 30],
      pitch: 'Forecast daily demand two weeks out — enough runway to place purchase orders before the shelf goes empty.',
    },
    sample: {
      filename: 'sample_inventory_demand.csv', targetColumn: 'units_demanded',
      description: 'Two years of daily demand with stock on hand, POs placed and received, supplier lead times and days of cover — supplier halts and port delays as events.',
    },
  },
  {
    id: 'finance', label: 'Revenue & finance', Icon: PaymentsIcon,
    workspaceName: 'Revenue Forecasting',
    targets: ['Revenue', 'Cash flow', 'New customers', 'Subscription MRR', 'Churn'],
    goalText: 'Forecast revenue and cash flow', goalName: 'Revenue — 30 days ahead',
    scenario: {
      goalType: 'forecast_value', horizonDays: 30, horizonOptions: [14, 30, 90],
      pitch: 'Forecast daily revenue a month out from orders, order value, acquisition spend and churn.',
    },
    sample: {
      filename: 'sample_daily_revenue.csv', targetColumn: 'revenue_usd',
      description: 'Two years of daily revenue with orders, order value, new customers, churn, marketing spend — and business events like big client wins.',
    },
  },
  {
    id: 'cashflow', label: 'Cash flow', Icon: AccountBalanceIcon,
    workspaceName: 'Cash Flow Forecasting',
    targets: ['Cash balance', 'Receipts', 'Net cash flow', 'AR outstanding', 'Runway'],
    goalText: 'Forecast cash position and receipts', goalName: 'Cash balance — 1 month ahead',
    scenario: {
      goalType: 'forecast_value', horizonDays: 30, horizonOptions: [30, 60, 90],
      pitch: 'Forecast the cash position a month out — receipts trail invoices by ~30 days, payroll and rent land on the clock.',
    },
    sample: {
      filename: 'sample_cash_flow.csv', targetColumn: 'cash_balance_kusd',
      description: 'Two years of daily cash balance with receipts, invoices issued, payroll runs, operating outflows and AR outstanding — receipts trail invoices by ~30 days.',
    },
  },
  {
    id: 'traffic', label: 'Web & app traffic', Icon: LanguageIcon,
    workspaceName: 'Traffic Forecasting',
    targets: ['Page views', 'Sign-ups & conversions', 'Active users', 'Ad performance', 'Server load'],
    goalText: 'Forecast website and app traffic', goalName: 'Page views — 2 weeks ahead',
    scenario: {
      goalType: 'forecast_value', horizonDays: 14, horizonOptions: [7, 14, 30],
      pitch: 'Forecast daily page views two weeks out from ad spend, email sends, social buzz and viral moments.',
    },
    sample: {
      filename: 'sample_web_traffic.csv', targetColumn: 'page_views',
      description: 'Two years of daily page views with visitors, ad spend, email sends, social mentions, bounce rate — and viral moments (HN, TikTok, Reddit) as events.',
    },
  },
  {
    id: 'tourism', label: 'Travel & hospitality', Icon: HotelIcon,
    workspaceName: 'Occupancy Forecasting',
    targets: ['Room occupancy', 'Bookings & cancellations', 'Average daily rate', 'Tourist arrivals', 'Flight demand'],
    goalText: 'Forecast occupancy and travel demand', goalName: 'Occupancy — 2 months ahead',
    scenario: {
      goalType: 'forecast_value', horizonDays: 60, horizonOptions: [30, 60, 90],
      pitch: 'Forecast occupancy two months out — flight searches lead stays by three weeks, matching your booking window.',
    },
    sample: {
      filename: 'sample_hotel_occupancy.csv', targetColumn: 'occupancy_rate_pct',
      description: 'Two years of daily hotel occupancy with rates, flight searches (which lead stays by ~3 weeks), booking lead times, arrivals — festivals as events.',
    },
  },
  {
    id: 'gaming', label: 'Gaming & apps', Icon: SportsEsportsIcon,
    workspaceName: 'Player Engagement Forecasting',
    targets: ['Daily active users', 'New installs', 'In-app purchases', 'Session length', 'Player churn'],
    goalText: 'Forecast player activity and engagement', goalName: 'Daily active users — 1 month ahead',
    scenario: {
      goalType: 'forecast_value', horizonDays: 30, horizonOptions: [14, 30, 60],
      pitch: 'Forecast DAU a month out from installs, ad spend, Twitch viewership and content-update cycles.',
    },
    sample: {
      filename: 'sample_game_dau.csv', targetColumn: 'daily_active_users',
      description: 'Two years of daily active users with installs, session length, ad spend, Twitch viewers, IAP revenue, update cadence — season launches, streamer moments and outages as events.',
    },
  },
  {
    id: 'manufacturing', label: 'Manufacturing & IoT', Icon: PrecisionManufacturingIcon,
    workspaceName: 'Production Forecasting',
    targets: ['Units produced', 'Downtime & failures', 'Defect rate', 'Sensor anomalies', 'Maintenance needs'],
    goalText: 'Forecast production output and downtime', goalName: 'Units produced — 2 weeks ahead',
    scenario: {
      goalType: 'forecast_value', horizonDays: 14, horizonOptions: [7, 14, 30],
      pitch: 'Forecast factory output two weeks out — vibration and defect rates foreshadow the breakdowns that cost you shifts.',
      // The flagship fleet demo: 18 machine sensors, three of which are
      // quietly degrading — the risk leaderboard surfaces them on day one.
      defaultScope: 'fleet',
      fleet: {
        goalType: 'rank_by_risk', horizonDays: 7,
        selector: { type: 'pattern', value: 'sensor_*_vibration' },
        goalName: 'Machine sensors — rank by failure risk',
        membersLabel: 'sensors',
        pitch: 'Rank all 18 machine sensors by breach risk — a live leaderboard of which machines are heading for a breakdown, so maintenance goes where it matters.',
      },
    },
    sample: {
      filename: 'sample_factory_output.csv', targetColumn: 'units_produced',
      description: 'Two years of daily factory output with downtime, defect rates, material costs, order backlog and an 18-sensor vibration fleet — a few machines are quietly wearing toward failure.',
    },
  },
  {
    id: 'energy', label: 'Energy & utilities', Icon: BoltIcon,
    workspaceName: 'Grid Demand Forecasting',
    targets: ['Grid demand', 'Renewable generation', 'Energy prices', 'Peak load', 'Outage risk'],
    goalText: 'Forecast energy demand and generation', goalName: 'Grid demand — 2 weeks ahead',
    scenario: {
      goalType: 'forecast_value', horizonDays: 14, horizonOptions: [7, 14, 30],
      pitch: 'Forecast grid load two weeks out from temperature (the classic U-curve), renewables output and industrial activity.',
    },
    sample: {
      filename: 'sample_grid_demand.csv', targetColumn: 'grid_demand_mwh',
      description: 'Two years of daily grid load with temperature, humidity, solar & wind output, industrial activity and heatwave events.',
    },
  },
  {
    id: 'logistics', label: 'Supply chain & logistics', Icon: LocalShippingIcon,
    workspaceName: 'Supply Chain Forecasting',
    targets: ['Freight rates', 'Shipment volume', 'Lead times', 'Warehouse throughput', 'Stockout risk'],
    goalText: 'Forecast freight rates and supply chain risk', goalName: 'Container rate — 1 month ahead',
    scenario: {
      goalType: 'forecast_value', horizonDays: 30, horizonOptions: [14, 30, 60],
      pitch: 'Forecast container rates a month out from congestion, vessel queues, fuel and warehouse utilization.',
    },
    sample: {
      filename: 'sample_freight_rates.csv', targetColumn: 'container_rate_usd',
      description: 'Two years of container rates with port congestion, vessels waiting, fuel, order flow and warehouse utilization — canal blockages and strikes spike rates for weeks.',
    },
  },
  {
    id: 'transportation', label: 'Transportation & mobility', Icon: DirectionsBusIcon,
    workspaceName: 'Mobility Forecasting',
    targets: ['Daily ridership', 'Delivery times', 'Fleet utilization', 'Route demand', 'Fuel use'],
    goalText: 'Forecast ridership and mobility demand', goalName: 'Daily riders — 2 weeks ahead',
    scenario: {
      goalType: 'forecast_value', horizonDays: 14, horizonOptions: [7, 14, 30],
      pitch: 'Forecast metro ridership two weeks out from fuel prices, weather, school calendars and city events.',
    },
    sample: {
      filename: 'sample_metro_ridership.csv', targetColumn: 'daily_riders',
      description: 'Two years of daily metro ridership with fuel prices, rain, temperature, school days, downtown events — and concerts, marathons and strikes as events.',
    },
  },
  {
    id: 'staffing', label: 'Workforce & staffing', Icon: SupportAgentIcon,
    workspaceName: 'Staffing Forecasting',
    targets: ['Call volume', 'Staffing needs', 'Service level', 'Handle time', 'Shift coverage'],
    goalText: 'Forecast call volume and staffing needs', goalName: 'Calls offered — 1 week ahead',
    scenario: {
      goalType: 'forecast_value', horizonDays: 7, horizonOptions: [3, 7, 14],
      pitch: 'Forecast hourly call volume a week out — the demand curve your WFM team builds shift schedules from.',
    },
    sample: {
      filename: 'sample_contact_center_calls.csv', targetColumn: 'calls_offered',
      description: 'Six months of HOURLY contact-center calls with agents scheduled, handle time, service level and campaign days — outages and TV ads as events. The only sub-daily sample.',
    },
  },
  {
    id: 'telecom', label: 'Telecom & networks', Icon: CellTowerIcon,
    workspaceName: 'Network Traffic Forecasting',
    targets: ['Data traffic', 'Network congestion', 'Subscriber growth', 'Churn', 'Outage risk'],
    goalText: 'Forecast network traffic and load', goalName: 'Data traffic — 2 weeks ahead',
    scenario: {
      goalType: 'forecast_value', horizonDays: 14, horizonOptions: [7, 14, 30],
      pitch: 'Forecast network load two weeks out from subscriber growth, streaming shares and premiere nights.',
    },
    sample: {
      filename: 'sample_network_traffic.csv', targetColumn: 'data_traffic_tb',
      description: 'Two years of daily network traffic with subscribers, video & gaming shares, speeds and outage minutes — streaming premieres and outages as events.',
    },
  },
  {
    id: 'fraud', label: 'Fraud prevention', Icon: CreditCardOffIcon,
    workspaceName: 'Fraud Risk Forecasting',
    targets: ['Fraud attempts', 'Chargebacks', 'Account takeovers', 'Fraud losses'],
    goalText: 'Forecast and prevent fraud attempts', goalName: 'Fraud attempts — 2 weeks ahead',
    scenario: {
      goalType: 'forecast_value', horizonDays: 14, horizonOptions: [7, 14, 30],
      pitch: 'Forecast fraud attempts two weeks out — breach headlines, login failures and VPN traffic lead the attack waves.',
    },
    sample: {
      filename: 'sample_fraud_attempts.csv', targetColumn: 'fraud_attempts',
      description: 'Two years of daily fraud attempts vs transactions, login failures, VPN share, transaction size — data-breach headlines spike attacks; chargebacks lag by a week.',
    },
  },
  {
    id: 'cybersecurity', label: 'Cybersecurity', Icon: SecurityIcon,
    workspaceName: 'Threat Forecasting',
    targets: ['Intrusion attempts', 'Phishing volume', 'Malware detections', 'Incident load'],
    goalText: 'Forecast cyber threats and attack volume', goalName: 'Intrusion attempts — 2 weeks ahead',
    scenario: {
      goalType: 'forecast_value', horizonDays: 14, horizonOptions: [7, 14, 30],
      pitch: 'Forecast intrusion volume two weeks out — dark-web chatter and fresh CVEs lead the attack waves.',
    },
    sample: {
      filename: 'sample_intrusion_attempts.csv', targetColumn: 'intrusion_attempts',
      description: 'Two years of daily intrusion attempts with CVEs, phishing, malware detections, failed logins, patch compliance and dark-web chatter — zero-days as events.',
    },
  },
  {
    id: 'insurance', label: 'Insurance', Icon: UmbrellaIcon,
    workspaceName: 'Claims Forecasting',
    targets: ['Claims filed', 'Claim severity', 'Policy lapses', 'Catastrophe losses'],
    goalText: 'Forecast claims volume', goalName: 'Claims filed — 3 weeks ahead',
    scenario: {
      goalType: 'forecast_value', horizonDays: 21, horizonOptions: [7, 21, 30],
      pitch: 'Forecast claims volume three weeks out — storms drive claims for days after they hit.',
    },
    sample: {
      filename: 'sample_insurance_claims.csv', targetColumn: 'claims_filed',
      description: 'Two years of daily claims with storm severity, rainfall, wind and policy counts — hailstorms drive claims for days after they hit.',
    },
  },
  {
    id: 'weather', label: 'Weather & climate', Icon: WbSunnyIcon,
    workspaceName: 'Weather Forecasting',
    targets: ['Temperature', 'Rainfall', 'Wind & storms', 'Air quality', 'Extreme-event risk'],
    goalText: 'Forecast weather conditions', goalName: 'Max temperature — 10-day outlook',
    scenario: {
      goalType: 'forecast_value', horizonDays: 10, horizonOptions: [5, 10, 14],
      pitch: 'The cleanest demo of driver discovery: pressure drops today mean rain tomorrow — watch the platform find it, then hold a real 10-day outlook.',
    },
    sample: {
      filename: 'sample_city_weather.csv', targetColumn: 'max_temp_c',
      description: 'Two years of daily temperature, humidity, wind, pressure, cloud cover and rainfall — a textbook 1-day lead (pressure drop → next-day rain) for discovery to find.',
    },
  },
  {
    id: 'healthcare', label: 'Healthcare', Icon: LocalHospitalIcon,
    workspaceName: 'Patient Demand Forecasting',
    targets: ['ER admissions', 'Bed occupancy', 'Appointment no-shows', 'Medication demand', 'Staffing needs'],
    goalText: 'Forecast patient demand and admissions', goalName: 'ER admissions — 3 weeks ahead',
    scenario: {
      goalType: 'forecast_value', horizonDays: 21, horizonOptions: [7, 21, 30],
      pitch: 'Forecast ER admissions three weeks out — wastewater signals lead reported flu, which drives the winter surge.',
    },
    sample: {
      filename: 'sample_er_admissions.csv', targetColumn: 'er_admissions',
      description: 'Two years of daily ER admissions with flu counts, wastewater viral load, air quality, pollen and heat — plus outbreak alerts as events.',
    },
  },
  {
    id: 'stocks', label: 'Stocks & markets', Icon: ShowChartIcon,
    workspaceName: 'Equity Market Forecasting',
    targets: ['Stock close price', 'Volatility', 'Trading volume', 'Index level'],
    goalText: 'Forecast stock prices and volatility', goalName: 'Close price — 10 trading days ahead',
    scenario: {
      goalType: 'forecast_distribution', horizonDays: 10, horizonOptions: [5, 10, 21],
      pitch: 'Forecast the likely price range ten trading days out from market factors, rates, volatility and headlines.',
    },
    sample: {
      filename: 'sample_stock_prices.csv', targetColumn: 'close_price_usd',
      description: 'Two years of daily closes with volume, S&P 500, sector index, VIX, 10-year yields, earnings weeks — and market-moving headlines as events.',
    },
  },
  {
    id: 'realestate', label: 'Real estate', Icon: HomeWorkIcon,
    workspaceName: 'Housing Market Forecasting',
    targets: ['Median list price', 'Days on market', 'Sales volume', 'Rental rates', 'Mortgage demand'],
    goalText: 'Forecast property prices and market activity', goalName: 'Median list price — 3 months ahead',
    scenario: {
      goalType: 'forecast_value', horizonDays: 90, horizonOptions: [30, 90, 180],
      pitch: 'Forecast median list prices a quarter out — mortgage rates and buyer search interest lead the market.',
    },
    sample: {
      filename: 'sample_housing_market.csv', targetColumn: 'median_list_price_usd',
      description: 'Two years of daily listing prices with mortgage rates, sales, days-on-market, price per sqft, buyer search interest and rate-decision events.',
    },
  },
  {
    id: 'agriculture', label: 'Agriculture & food', Icon: AgricultureIcon,
    workspaceName: 'Crop Market Forecasting',
    targets: ['Crop prices', 'Yield per hectare', 'Commodity demand', 'Input costs', 'Livestock prices'],
    goalText: 'Forecast crop prices and yields', goalName: 'Wheat price — 2 months ahead',
    scenario: {
      goalType: 'forecast_value', horizonDays: 60, horizonOptions: [30, 60, 90],
      pitch: 'Forecast wheat prices two months out from rainfall, soil moisture, input costs and export flows.',
    },
    sample: {
      filename: 'sample_wheat_prices.csv', targetColumn: 'wheat_price_usd_bushel',
      description: 'Two years of daily wheat prices with rainfall, temperature, soil moisture, fertilizer costs and export volumes — export bans and droughts as events.',
    },
  },
  {
    id: 'crypto', label: 'Crypto', Icon: CurrencyBitcoinIcon,
    workspaceName: 'Crypto Market Forecasting',
    targets: ['BTC price', 'Altcoin prices', 'Trading volume', 'Network activity'],
    goalText: 'Forecast crypto prices and activity', goalName: 'BTC price — 2 weeks ahead',
    scenario: {
      goalType: 'forecast_distribution', horizonDays: 14, horizonOptions: [7, 14, 30],
      pitch: 'Forecast the BTC price band two weeks out from on-chain activity, funding, sentiment and market headlines.',
    },
    sample: {
      filename: 'sample_crypto_prices.csv', targetColumn: 'btc_price_usd',
      description: 'Two years of daily BTC prices with volume, wallets, funding rates, exchange inflows, Google trends, fear & greed — and market headlines & whale moves as events.',
    },
  },
  {
    id: 'sports', label: 'Sports', Icon: SportsSoccerIcon,
    workspaceName: 'Fan Demand Forecasting',
    targets: ['Merchandise sales', 'Ticket sales & attendance', 'Fan engagement', 'Concession revenue'],
    goalText: 'Forecast fan demand and merchandise sales', goalName: 'Merch sales — 2 weeks ahead',
    scenario: {
      goalType: 'forecast_value', horizonDays: 14, horizonOptions: [7, 14, 30],
      pitch: 'Forecast merch sales two weeks out from match results, league position and fan buzz — wins sell shirts.',
    },
    sample: {
      filename: 'sample_fan_merchandise.csv', targetColumn: 'merch_sales_usd',
      description: 'Two years of daily merch sales with social buzz, ticket & jersey searches, league position — match results as events (wins sell shirts).',
    },
  },
  {
    id: 'other', label: 'Something else', Icon: AutoAwesomeIcon,
    workspaceName: 'My Forecasting Workspace',
    goalText: '', goalName: 'Daily customers — 2 weeks ahead',
    scenario: {
      goalType: 'forecast_value', horizonDays: 14, horizonOptions: [7, 14, 30],
      pitch: 'Forecast daily customers two weeks out from foot traffic, marketing, weather and local events.',
    },
    sample: {
      filename: 'sample_daily_customers.csv', targetColumn: 'daily_customers',
      description: 'Two years of daily customer counts for a local business with foot traffic, marketing spend, ticket size, weather, holidays and local events.',
    },
  },
];

// Thematic grouping for the onboarding picker. Each group carries a muted
// accent color used for its tiles — enough color to scan by, not a rainbow.
// "other" is intentionally absent: it renders as its own full-width tile.
// Group ORDER is deliberate: commercial planning first, then the operations
// verticals the site's solution pages sell to; markets/fan cases sit last so
// the second row a churn-prone trial user scans is ICP, not speculation.
export const USE_CASE_GROUPS = [
  { id: 'business', label: 'Business & growth', accent: '#0E8A6D', ids: ['sales', 'inventory', 'finance', 'cashflow', 'traffic', 'tourism', 'gaming'] },
  { id: 'operations', label: 'Operations & industry', accent: '#B45309', ids: ['manufacturing', 'energy', 'logistics', 'transportation', 'staffing', 'telecom'] },
  { id: 'risk', label: 'Risk & security', accent: '#BE3455', ids: ['fraud', 'cybersecurity', 'insurance'] },
  { id: 'society', label: 'Environment & society', accent: '#0369A1', ids: ['weather', 'healthcare'] },
  { id: 'markets', label: 'Markets & trends', accent: '#5B5BD6', ids: ['stocks', 'realestate', 'agriculture', 'crypto', 'sports'] },
];

export const getUseCaseById = (id) =>
  USE_CASES.find((u) => u.id === id) || USE_CASES[USE_CASES.length - 1];

// Derive the workspace name from the picked target (or free text), so
// the setup wizard can pre-fill an editable name that matches what the
// user actually chose - never a presumptuous default.
export const deriveWorkspaceName = (useCaseId, freeText = '', target = '') => {
  const uc = getUseCaseById(useCaseId);
  const src = (target || freeText || '').trim();
  if (!src) return uc.workspaceName;
  const words = src.split(/\s+/).slice(0, 4)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1));
  return `${words.join(' ')} Forecasting`;
};

// Tangible horizon labels ("2 weeks", not "14 bins").
export const fmtHorizonDays = (days) => {
  if (days % 30 === 0) return days === 30 ? '1 month' : `${days / 30} months`;
  if (days % 7 === 0) return days === 7 ? '1 week' : `${days / 7} weeks`;
  return `${days} days`;
};

// Columns whose text should be scored as EVENTS (not stored as strings).
// Convention: every generator puts exactly one such column last, named to
// match this pattern.
export const isEventColumn = (name) =>
  /(_event|_news|_intel_event)$/.test(name) || name === 'market_news';

/**
 * Build the sample CSV for a use case. Returns { filename, csvText,
 * targetColumn, eventColumns, header, rows } — the caller stages it via
 * inspect-file and imports with explicit field mappings so event columns
 * register as events. `header`/`rows` are the raw values behind csvText,
 * so callers can preview exactly what was imported without re-parsing.
 */
export function buildSampleCsv(useCaseId) {
  const uc = getUseCaseById(useCaseId);
  const rng = mulberry32(42);
  const randn = makeRandn(rng);
  const gen = GENERATORS[uc.id] || GENERATORS.other;
  const { header, rows } = gen(rng, randn);
  const csvText = [
    header.join(','),
    ...rows.map((r) => r.map(csvField).join(',')),
  ].join('\n');
  return {
    filename: uc.sample.filename,
    csvText,
    targetColumn: uc.sample.targetColumn,
    eventColumns: header.filter(isEventColumn),
    header,
    rows,
  };
}
